import { Form, Formik } from 'formik';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { Select } from '~components/form';
import Typography from '~components/typography';
import {
  BathroomArrangementOptions,
  KitchenArrangementOptions,
  RoomCategoryOptions,
  TenancyLengthOptions,
} from '~graphql/__generated__/globalTypes';
import { SP_QueryRoomTypeAvailability_getRoomTypeAvailability } from '~graphql/__generated__/SP_QueryRoomTypeAvailability';
import { useTranslation } from '~i18n';
import {
  IconBathroom,
  IconDatepicker,
  IconInfo,
  IconKitchen,
  IconRight,
  IconRoom,
} from '~svg-components';
import styles from './filter-selector-form.module.scss';
import TenancyOptionSelect from './tenancy-option-select';

export interface IDate {
  startDate: any;
  endDate: any;
}
interface Option {
  label: string;
  value: string;
}
interface RoomTypeProps {
  id: string | null;
  originId: number | null;
  name: any;
  kitchenArrangement: KitchenArrangementOptions | null;
  bathroomArrangement: BathroomArrangementOptions | null;
  category: RoomCategoryOptions;
  hasPrice: boolean | null;
}

interface IFilterSelectorProps {
  roomTypes: (RoomTypeProps | null)[] | null;
  roomTypeId: string;
  tenancyOptionId: string;
  tenancyDate: IDate;
  academicYearInfos?: SP_QueryRoomTypeAvailability_getRoomTypeAvailability['academicYearInfos'];
  setRoomTypeId: (value: string) => void;
  setAcademicYearId: (value: string) => void;
  setTenancyDate: (value: IDate) => void;
  setTenancyOptionId: (value: string) => void;
  setTenancyOptionRateType: (value: string) => void;
}

const FilterSelectorForm: React.FC<IFilterSelectorProps> = ({
  roomTypes,
  roomTypeId,
  tenancyOptionId,
  tenancyDate,
  academicYearInfos,
  setRoomTypeId,
  setAcademicYearId,
  setTenancyOptionId,
  setTenancyOptionRateType,
  setTenancyDate,
}) => {
  const { t } = useTranslation('student_portal');
  const [tenancyOptionLengthType, setTenancyOptionLengthType] = useState('');

  const roomTypeOptions = useMemo(() => {
    return roomTypes
      ?.map(item => {
        return {
          label: item?.name,
          value: item?.id,
        };
      })
      .sort((current, next) => {
        return current.label?.toLowerCase() > next.label?.toLowerCase() ? 1 : -1;
      });
  }, [roomTypes]);

  const roomTypeSelected = useMemo(() => {
    return roomTypes?.find(item => item?.id === roomTypeId);
  }, [roomTypeId, roomTypes]);

  const initRoomType = roomTypeSelected
    ? {
        label: roomTypeSelected.name,
        value: roomTypeSelected.id,
      }
    : '';

  return (
    <div>
      <div className={styles.root}>
        <Formik initialValues={{ roomTypes: initRoomType }} onSubmit={() => {}}>
          <Form className={styles.form}>
            <div className={styles.inputWrapper}>
              <Select
                className={styles.input}
                selectType="custom"
                placeholder={t('select_room_type')}
                options={roomTypeOptions}
                menuShouldScrollIntoView={false}
                label={t('select_bed.room_type.title')}
                name="roomTypes"
                onSelect={(vl: Option) => {
                  if (vl) setRoomTypeId(vl.value);
                }}
              />
              {roomTypeSelected && (
                <div className={styles.result}>
                  {roomTypeSelected?.category && (
                    <div className={styles.result__item}>
                      <IconRoom width="28" height="28" />
                      <Typography variant="body3" style={{ marginLeft: 4 }}>
                        {t(`select_bed.room.${roomTypeSelected.category.toLowerCase()}`)}
                      </Typography>
                    </div>
                  )}
                  {roomTypeSelected?.bathroomArrangement && (
                    <div className={styles.result__item}>
                      <IconBathroom width="28" height="28" />
                      <Typography variant="body3" style={{ marginLeft: 4 }}>
                        {t(
                          `select_bed.bathroom.${roomTypeSelected?.bathroomArrangement?.toLowerCase()}`
                        )}
                      </Typography>
                    </div>
                  )}
                  {roomTypeSelected?.kitchenArrangement && (
                    <div className={styles.result__item}>
                      <IconKitchen width="28" height="28" />
                      <Typography variant="body3" style={{ marginLeft: 4 }}>
                        {t(
                          `select_bed.kitchen.${roomTypeSelected?.kitchenArrangement?.toLowerCase()}`
                        )}
                      </Typography>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className={styles.inputWrapper}>
              <TenancyOptionSelect
                tenancyOptionId={tenancyOptionId}
                setTenancyOptionId={(id: string) => {
                  setTenancyOptionId(id);
                }}
                setAcademicYearId={(id: string) => {
                  setAcademicYearId(id);
                }}
                setTenancyDate={(value: IDate) => {
                  setTenancyDate(value);
                }}
                setTenancyOptionRateType={(value: any) => {
                  setTenancyOptionRateType(value);
                }}
                setTenancyOptionLengthType={(value: any) => {
                  setTenancyOptionLengthType(value);
                }}
                tenancyDate={tenancyDate}
                academicYearInfos={academicYearInfos}
              />
              {tenancyDate?.endDate && tenancyDate.startDate && (
                <div className={styles.result}>
                  <div className={styles.result__tenancy}>
                    <IconDatepicker width="15" height="16" fill="var(--color-purple_4)" />
                    <div className={styles.result__tenancyLength}>
                      {tenancyOptionLengthType === TenancyLengthOptions.ROLLING && (
                        <Typography variant="body3">
                          {t('from')} {moment(tenancyDate.startDate).format('ddd, ll')}
                        </Typography>
                      )}

                      {tenancyOptionLengthType !== TenancyLengthOptions.ROLLING && (
                        <>
                          <Typography variant="body3">
                            {moment(tenancyDate.startDate).format('ddd, ll')}
                          </Typography>
                          <IconRight
                            width="12"
                            height="12"
                            fill="var(--color-purple_6)"
                            style={{ margin: '0 8px' }}
                          />
                          <Typography variant="body3">
                            {moment(tenancyDate.endDate).format('ddd, ll')}
                          </Typography>
                        </>
                      )}
                    </div>
                  </div>

                  {tenancyOptionLengthType === TenancyLengthOptions.ROLLING && (
                    <div className={styles.result__tenancy}>
                      <IconInfo fill="var(--color-purple_4)" width="16" height="16" />
                      <div className={styles.result__tenancyLength}>
                        <Typography variant="body3" color="var(--color-purple_4)">
                          {t('with_no_end_data')}
                        </Typography>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Form>
        </Formik>
      </div>
    </div>
  );
};

export default FilterSelectorForm;
